// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Providing and sharing, § Decisions (downloads)
// ==============================================
// /papers/requests/:rid/share — Crew-5. The crew member chooses when the
// skipper's access ends (arrival, the skipper's suggestion, or a date), may set
// a different date for any one paper, reads what sharing means — including that
// downloaded copies stay with the skipper — and shares. share.ts holds the rule.
// The download wording matches what step 3.3 built: every copy the skipper
// downloads is a PDF stamped with their name, the date and the last day of
// access, and each view, download and crew-list use shows in the activity.
// ==============================================

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../services/api';
import { useCrewRequest, useSetCrewRequest } from '../hooks';
import { apiError } from '../apiError';
import { defaultShareChoice, maxEndDate, shareOptions, shareableItems, sharePayload } from '../share';
import type { ShareChoice } from '../share';
import { isAnswered, isWritable } from '../itemDisplay';
import { firstName, fmtDay, today } from '../dates';
import { C, CrewShell, ErrorNote, Loading, PrimaryButton, StepHeading, Tick } from '../ui';

const optClass = (on: boolean) =>
  `flex min-h-[56px] w-full items-center justify-between gap-2.5 rounded-xl px-4 text-left text-base font-semibold text-[#e8eef8] ${on ? 'border-2 border-[#5aa9ff] bg-[#0f2856]' : 'border border-[#2a4a86]'} ${C.focus}`;

export function CrewSharePage() {
  const { rid = '' } = useParams();
  const navigate = useNavigate();
  const q = useCrewRequest(rid);
  const setView = useSetCrewRequest(rid);
  const options = useMemo(() => (q.data ? shareOptions(q.data.request) : []), [q.data]);
  const [choice, setChoice] = useState<ShareChoice | null>(null);
  const [perPaper, setPerPaper] = useState(false);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [error, setError] = useState<{ text: string; itemId: string | null } | null>(null);
  const [busy, setBusy] = useState(false);

  if (q.isLoading) return <CrewShell><Loading /></CrewShell>;
  if (q.error || !q.data) return <CrewShell back={{ to: `/papers/requests/${rid}`, label: 'Back to the list' }}><ErrorNote>{apiError(q.error, 'This request could not be loaded').message}</ErrorNote></CrewShell>;

  const { request: req, items } = q.data;
  const who = firstName(req.requesterName);
  const ready = shareableItems(items);
  const todo = items.filter((i) => !isAnswered(i));
  const answeredCount = items.filter(isAnswered).length;
  const chosen = choice ?? defaultShareChoice(options);
  const todayDate = today(req.timezone);
  const back = { to: `/papers/requests/${rid}`, label: 'Back to the list' };

  if (!isWritable(req) || ready.length === 0) {
    return (
      <CrewShell back={back}>
        <StepHeading focusKey="nothing">Nothing new to share</StepHeading>
        <p className={`m-0 text-base leading-relaxed ${C.soft}`}>{isWritable(req) ? 'Add a paper first, then come back here to share it.' : 'This request is closed.'}</p>
        <Link to={`/papers/requests/${rid}/shared`} className={`min-h-[44px] self-start py-2 text-base ${C.link}`}>See what you shared</Link>
      </CrewShell>
    );
  }

  const share = async () => {
    const p = sharePayload(chosen, options, perPaper ? overrides : {}, todayDate);
    if (!p.ok) {
      setError({ text: p.error, itemId: p.itemId });
      document.getElementById(p.itemId ? `share-date-${p.itemId}` : 'share-pick')?.focus();
      return;
    }
    setBusy(true); setError(null);
    try {
      setView(await api.crewShare(rid, p.body));
      navigate(`/papers/requests/${rid}/shared`);
    } catch (err) {
      setError({ text: apiError(err, 'Sharing didn’t go through. Try again.').message, itemId: null });
      setBusy(false);
    }
  };

  return (
    <CrewShell back={back}>
      <div className="flex flex-col gap-1.5">
        <span className={C.label}>{answeredCount} of {items.length} ready</span>
        <StepHeading focusKey="share">Share with {who}</StepHeading>
      </div>

      <ul className="m-0 flex list-none flex-col p-0">
        {ready.map((i) => <li key={i.id} className="flex items-center gap-3 border-b border-[#16305e] py-2.5 text-base"><Tick />{i.label}</li>)}
      </ul>
      {todo.length > 0 && <p className={`m-0 text-[15px] leading-relaxed ${C.soft}`}>Still to add: {todo.map((i) => i.label).join(', ')}. You can share these now and the rest later.</p>}

      <fieldset className="m-0 flex flex-col gap-2.5 border-0 p-0">
        <legend className="mb-2.5 p-0 text-base font-bold">{who} can see them until</legend>
        {options.map((o) => {
          const on = chosen.kind === o.kind;
          return (
            <button key={o.kind} type="button" aria-pressed={on} onClick={() => { setChoice({ kind: o.kind }); setError(null); }} className={optClass(on)}>
              <span className="flex flex-col gap-0.5"><span>{o.label}</span>{o.sublabel && <span className="text-[13px] font-medium text-[#9fcbff]">{o.sublabel}</span>}</span>
              <span className={`font-mono text-[15px] font-medium ${on ? 'text-white' : C.muted}`}>{fmtDay(o.date)}</span>
            </button>
          );
        })}
        <button type="button" aria-pressed={chosen.kind === 'date'} onClick={() => { setChoice({ kind: 'date', date: chosen.kind === 'date' ? chosen.date : '' }); setError(null); }} className={optClass(chosen.kind === 'date')}>
          <span>Pick a date</span>
          {chosen.kind === 'date' && chosen.date ? <span className="font-mono text-[15px] font-medium text-white">{fmtDay(chosen.date)}</span> : <span aria-hidden="true">›</span>}
        </button>
        {chosen.kind === 'date' && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="share-pick" className="text-sm font-semibold text-[#c7d3e6]">Access ends after</label>
            <input id="share-pick" type="date" min={todayDate} max={maxEndDate(todayDate)} value={chosen.date} onChange={(e) => setChoice({ kind: 'date', date: e.target.value })} className={C.input} />
          </div>
        )}
        <button type="button" aria-expanded={perPaper} onClick={() => setPerPaper(!perPaper)} className={`min-h-[44px] self-start py-1.5 text-left text-[15px] ${C.link} ${C.focus}`}>
          {perPaper ? 'Use one date for every paper' : 'A different date for one paper'}
        </button>
        {perPaper && (
          <div className={`${C.card} flex flex-col gap-3 p-4`}>
            <p className={`m-0 text-sm ${C.muted}`}>Leave a paper blank to use the date above.</p>
            {ready.map((i) => (
              <div key={i.id} className="flex flex-col gap-1.5">
                <label htmlFor={`share-date-${i.id}`} className="text-sm font-semibold text-[#c7d3e6]">{i.label}</label>
                <input id={`share-date-${i.id}`} type="date" min={todayDate} max={maxEndDate(todayDate)} value={overrides[i.id] ?? ''}
                  onChange={(e) => setOverrides({ ...overrides, [i.id]: e.target.value })} className={C.input}
                  aria-invalid={error?.itemId === i.id ? true : undefined} />
              </div>
            ))}
          </div>
        )}
      </fieldset>

      <p className={`m-0 text-[15px] leading-relaxed ${C.soft}`}>
        {who} can view them and use your details on the crew list for Customs. {who} can also download copies, each marked with {who}’s name, the date and when access ends; any {who} keeps stay with {who} after access ends. You’ll see every time {who} opens or downloads one, or makes the crew list, and you can withdraw at any time.
      </p>

      <ErrorNote>{error?.text}</ErrorNote>
      <div className="mt-auto flex flex-col gap-2">
        <PrimaryButton onClick={() => void share()} busy={busy}>{busy ? 'Sharing…' : `Share with ${who}`}</PrimaryButton>
        <span className={`text-center text-sm ${C.muted}`}>They stay in your own papers, ready for the next passage.</span>
      </div>
    </CrewShell>
  );
}

export default CrewSharePage;
